import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    FiAlertCircle,
    FiAlertTriangle,
    FiCheckCircle,
    FiClock,
    FiEdit3,
    FiInfo,
    FiLoader,
    FiSettings,
    FiX,
} from "react-icons/fi";
import MessageContent from "../MessageContent";
import MessageInput from "../MessageInput";
import FollowupSuggestions from "../FollowupSuggestions";
import ActionControllerComponent from "../ActionControllerComponent";
import CitationsComponent from "../CitationsComponent";
import Tooltip from "../Tooltip";
import AssistantSettingsPanel from "../AssistantSettingsPanel";
import ConversationHistory from "../ConversationHistory";
import ConfirmationPrompt from "../ConfirmationPrompt";
import ConnectionNotice from "../ConnectionNotice";
import AssistantErrorBoundary from "../AssistantErrorBoundary";
import { AIAssistantAblyProvider } from "../../contexts/AIAssistantAblyContext";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import {
    selectIsChatOpen,
    selectIsChatExpanded,
} from "../../Selectors/AssistantSelector";
import {
    toggleChatVisibility,
    setChatExpanded,
    assistantResponding,
    deleteMessagesKeepingInitial,
    hideCitations,
    clearCitations,
    clearHistoryMetadata,
    clearFollowupSuggestions,
    setTaskStatusMessage,
    setWelcomeSuggestions,
    clearWelcomeSuggestions,
  setContextPanelTurn,
} from "../../state/actions";
import { useLouVersion } from "../../hooks/useLouVersion";
import { useScreenContext } from "../../hooks/useScreenContext";
import { useAssistantSettingsState } from "../../hooks/useAssistantSettingsState";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";
import { sendSuggestion } from "../../utils/sendSuggestion";
import { followUpsFor } from "../../utils/nextSteps";
import { tidyNarrative } from "../../utils/tidyNarrative";
import { useStatsigClient } from "@statsig/react-bindings";
import "./ChatWindow.scss";

const ToastStack = ({ toasts, onClose }) => {
    const typeStyles = {
        success: { icon: <FiCheckCircle />, className: "success", defaultDescription: "The action completed successfully." },
        error: { icon: <FiAlertCircle />, className: "error", defaultDescription: "Something went wrong." },
        warning: { icon: <FiAlertTriangle />, className: "warning", defaultDescription: "Please review this warning." },
        info: { icon: <FiInfo />, className: "info", defaultDescription: "FYI." },
    };

    return (
        <div className="chat-toast-stack">
            {toasts.map((toast) => {
                const style = typeStyles[toast.type] || typeStyles.info;
                return (
                    <div
                        key={toast.id}
                        className={`chat-toast-card ${style.className}`}
                    >
                        <div className="chat-toast-icon">{style.icon}</div>
                        <div className="chat-toast-content">
                            <div className="chat-toast-title">{toast.title}</div>
                            <div className="chat-toast-description">
                                {toast.description || style.defaultDescription}
                            </div>
                        </div>
                        <button
                            className="chat-toast-close"
                            type="button"
                            aria-label="Close toast"
                            onClick={() => onClose(toast.id)}
                        >
                            <FiX />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

const ChatWindowContent = () => {
    const dispatch = useDispatch();
    const louVersion = useLouVersion();
    const { startConversation } = useAssistantAbly();
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const isChatOpen = useSelector(selectIsChatOpen);
    const isExpanded = useSelector(selectIsChatExpanded);
    const messages = useSelector((state) => state.chat.messages);
    const userDetail = useSelector((state) => state.userDetail);
    const {
        citations,
        isActionControllerVisible,
        isCitationsExpanded,
        actionControllerContent,
        followupSuggestions = [],
    } = useSelector((state) => state.chat);
    const hasMessages = messages.length > 0;
    const isAssistantResponding = useSelector((state) => state.chat.isAssistantResponding);
    // Follow-ups for the latest reply, shown only here - replies no longer
    // carry them. Hidden while a reply is being written (the previous one's
    // questions are out of date by then) and once the user closes them.
    const lastReply = [...messages]
        .reverse()
        .find((m) => m.type !== "You" && (m.message_type || "text") === "text");
    const lastReplyKey = lastReply ? lastReply.turn_id ?? messages.lastIndexOf(lastReply) : null;
    const [dismissedReplyKey, setDismissedReplyKey] = useState(null);
    const followUps =
        isAssistantResponding || !lastReply || dismissedReplyKey === lastReplyKey
            ? []
            : followUpsFor(
                followupSuggestions,
                tidyNarrative(lastReply.payload?.text || lastReply.text || ""),
            );
    const [clearState, setClearState] = useState("idle");
    const [toasts, setToasts] = useState([]);
    const [isScrolledState, setIsScrolledState] = useState(false);
    const { buildCurrentScreenContext } = useScreenContext();
    const assistantSettings = useAssistantSettingsState({
        userId: userDetail?.id,
    });
    const { checkGate } = useStatsigClient();
    const isMultilingualAssistantEnabled = checkGate(
        AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_MULTILINGUAL_ENABLED,
    );

    React.useEffect(() => {
        const handleScrollState = (e) => {
            setIsScrolledState(e.detail.isScrolled);
        };
        window.addEventListener("chat-scroll-state", handleScrollState);
        return () => window.removeEventListener("chat-scroll-state", handleScrollState);
    }, []);

    const addToast = ({ type = "info", title, description, duration = 2500 }) => {
        const id = Math.random().toString(36).slice(2);
        const toast = { id, type, title, description };
        setToasts((prev) => [...prev, toast]);
        if (duration !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        }
        return id;
    };

    const updateToast = (id, updates) => {
        setToasts((prev) =>
            prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
        );
        if (updates.duration !== undefined && updates.duration !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, updates.duration);
        }
    };
    const handleClearChat = async () => {
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.NEW_CHAT_CLICKED);
        setClearState("clearing");
        try {
            // A new chat is a new conversation on the server, not a cleared
            // screen. The previous one stays exactly where it was and is
            // reachable from history — clearing it locally would have thrown
            // it away on this device and left it on every other one.
            const conversation = await startConversation();
            if (!conversation?.id) throw new Error("Clear chat failed");

            // The history overlay covers the transcript, so leaving it open
            // hides the very conversation this just created — the new chat is
            // made, and the user appears to still be in the list.
            setIsHistoryOpen(false);

            dispatch(clearCitations());
            dispatch(clearHistoryMetadata());
            dispatch(clearFollowupSuggestions());
            dispatch(clearWelcomeSuggestions());
            dispatch(assistantResponding(false));
            dispatch(setTaskStatusMessage({ type: "task_status_end" }));
            setClearState("idle");
            addToast({
                type: "success",
                title: "New chat ready.",
                description: "You can start typing your next question.",
                duration: 2000,
            });
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.NEW_CHAT_STARTED);
        } catch (error) {
            setClearState("failed");
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.NEW_CHAT_FAILED);
            addToast({
                type: "error",
                title: "Failed to start a new chat.",
                description: "Please try again.",
                duration: 2500,
            });
            setTimeout(() => {
                setClearState("idle");
            }, 2000);
        }
    };

    const toggleCitations = () => {
        if (isCitationsExpanded) {
            dispatch(hideCitations());
        }
    }

    const handleSuggestionClick = sendSuggestion;

    const handleExpand = () => {
        dispatch(setChatExpanded(!isExpanded));
    };

    const handleClose = () => {
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.CLOSED);
        dispatch(toggleChatVisibility(false));
        dispatch(setChatExpanded(false));
        assistantSettings.closeSettings();
    };

    return (
        <div
            className={` card-container chat-container
    ${isExpanded ? "expanded" : ""}
    ${!isChatOpen ? "collapsed" : ""}
    ${isActionControllerVisible ? "action-controller-visible" : ""}`}
        >


            <div className="card-header">
                <div className="card-title">
                    <span>{AI_ASSISTANT_CONSTANTS.CHAT.TITLE}</span>
                    {louVersion && (
                        <span className="beta-tag" aria-label="Assistant version">
                            {louVersion}
                        </span>
                    )}
                </div>

                <div className="card-header-actions">
                    {isMultilingualAssistantEnabled && (
                        <button
                            type="button"
                            className={`icon-button ${
                                assistantSettings.isSettingsOpen ? "is-active" : ""
                            }`}
                            onClick={() =>
                                assistantSettings.isSettingsOpen
                                    ? assistantSettings.closeSettings()
                                    : assistantSettings.openSettings()
                            }
                            aria-label="Open settings"
                            aria-pressed={assistantSettings.isSettingsOpen}
                        >
                            <Tooltip content="Settings" placement="bottom">
                                <span>
                                    <FiSettings size={16} />
                                </span>
                            </Tooltip>
                        </button>
                    )}

                    <button
                        type="button"
                        className={`icon-button ${isHistoryOpen ? "is-active" : ""}`}
                        onClick={() => {
                            // The context panel covers the same space history
                            // opens into, so opening one closes the other
                            // rather than stacking them out of sight.
                            dispatch(setContextPanelTurn(null));
                            setIsHistoryOpen((open) => !open);
                        }}
                        aria-label="Conversation history"
                        aria-pressed={isHistoryOpen}
                    >
                        <Tooltip content="History" placement="bottom">
                            <span>
                                <FiClock size={16} />
                            </span>
                        </Tooltip>
                    </button>

                    <button
                        type="button"
                        className={`icon-button ${clearState !== "idle" ? "disabled" : ""} ${
                            clearState === "clearing" ? "is-busy" : ""
                        }`}
                        onClick={clearState === "idle" ? handleClearChat : undefined}
                        disabled={clearState !== "idle"}
                        aria-label="Start new chat"
                        aria-busy={clearState === "clearing"}
                    >
                        <Tooltip
                            content={clearState === "clearing" ? "Starting a new chat…" : "New chat"}
                            placement="bottom"
                        >
                            <span>
                                {/* Creating the conversation takes a moment;
                                    the spinner shows the click was taken. */}
                                {clearState === "clearing" ? (
                                    <FiLoader size={16} className="icon-spin" data-testid="new-chat-spinner" />
                                ) : (
                                    <FiEdit3 size={16} />
                                )}
                            </span>
                        </Tooltip>
                    </button>

                    <button type="button" className="icon-button" onClick={handleClose} aria-label="Close chat">
                        <Tooltip content="Close" placement="bottom">
                            <span>
                                <FiX size={16} />
                            </span>
                        </Tooltip>
                    </button>
                </div>
            </div>

            {/* Between the header and the transcript: high enough to be read
                before the user types, low enough to leave the conversation
                visible behind it. */}
            <ConnectionNotice />

            <div className={`chat-body ${hasMessages ? "has-messages" : "no-messages"}`}>
                <MessageContent />
                <div className={`chat-footer-overlay ${isScrolledState ? "is-scrolled" : ""}`}>
                    {/* Above the composer, so the question it refers to is
                        still on screen when the user answers it. */}
                    <ConfirmationPrompt />
                    {followUps.length > 0 && (
                        <div className="followup-suggestions-footer">
                            <FollowupSuggestions
                                suggestions={followUps}
                                onSuggestionClick={handleSuggestionClick}
                                onClose={() => setDismissedReplyKey(lastReplyKey)}
                            />
                        </div>
                    )}
                    <MessageInput />
                </div>
            </div>
            {isHistoryOpen && (
                <div
                    className="lou-history-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Conversation history"
                >
                    <ConversationHistory onClose={() => setIsHistoryOpen(false)} />
                </div>
            )}
            {isMultilingualAssistantEnabled && assistantSettings.isSettingsOpen && (
                <div
                    className="assistant-settings-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Assistant settings"
                >
                    <AssistantSettingsPanel
                        preferencesDraft={assistantSettings.preferencesDraft}
                        onPreferencesChange={assistantSettings.setPreferencesDraft}
                        onClose={assistantSettings.closeSettings}
                    />
                </div>
            )}
            {isActionControllerVisible && (
                <ActionControllerComponent
                    title="Information Sources"
                    isVisible={isCitationsExpanded}
                    isExpanded={isCitationsExpanded}
                    toggleExpand={toggleCitations}
                >
                    {actionControllerContent === 'Citations' && <CitationsComponent citations={citations} />}
                </ActionControllerComponent>
            )}
            <ToastStack
                toasts={toasts}
                onClose={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
            />
        </div>
    );
};

/**
 * The boundary is outside the provider on purpose: a failure while the
 * realtime client is being set up has to be caught too, and the dashboard
 * around Lou must survive either way.
 */
const ChatWindow = () => (
    <AssistantErrorBoundary>
        <AIAssistantAblyProvider>
            <ChatWindowContent />
        </AIAssistantAblyProvider>
    </AssistantErrorBoundary>
);

export default ChatWindow;
